import { FC, useState, useMemo, useEffect, LegacyRef, RefObject } from 'react'
import cn from 'clsx'
import type { ProductVariant } from '@commerce/types/product'
import Image, { ImageProps } from 'next/image'
import WishlistButton from '@components/wishlist/WishlistButton'
import ProductTag from '../ProductTag'
import s from '../ProductCard/ProductCard.module.css'
import usePrice from '@framework/product/use-price'
import Link from 'next/link'
import { NextArrow, PrevArrow } from './ArrowsCarousel'
import { TryOn } from './TryOn'
import { InteractiveView } from './InteractiveView'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
//import 'slick-carousel/slick/slick-theme.css'
import { Button } from '@components/ui'
import { useCart } from '@framework/cart'
import { useAddItem as useAddItemCart } from '@framework/cart'
import { useRouter } from 'next/router'
import { gtagEvent } from '@utils/gtag-events'
import { imageLoader } from '@utils/image-loader'
import { useBreakpoints } from 'hooks/useTailwindBreakpoints'

interface Props {
  productVariant: ProductVariant
  className?: string
  noNameTag?: boolean
  imgProps?: Omit<ImageProps, 'src' | 'layout' | 'placeholder' | 'blurDataURL'>
  cardStyle?: 'default' | 'slim' | 'simple'
  type?: 'default' | 'relatedProducts'
  noLinkWrapper?: boolean
  revalidateProductsCache?: () => void
  inWishlist?: boolean
  onSelect?: (id: string | number) => void
  restorationRef?: RefObject<HTMLDivElement> | null
}

// Use this component if you want to render the details of a product variant.
// Otherwise use ProductCard component
const ProductVariantCard: FC<Props> = ({
  productVariant,
  imgProps,
  className,
  noNameTag = false,
  cardStyle = 'default',
  type = 'default',
  noLinkWrapper = false,
  revalidateProductsCache,
  inWishlist,
  onSelect,
  restorationRef,
}) => {
  const router = useRouter()
  const addItemCart = useAddItemCart()
  const { data } = useCart()
  const placeholderImage =
    process.env.NEXT_PUBLIC_SPREE_PRODUCT_PLACEHOLDER_IMAGE_URL ||
    '/product-img-placeholder.svg'
  const [isCardSelected, setIsCardSelected] = useState(false)
  const [loading, setLoading] = useState(false)
  const [imgError, setImgError] = useState(false)
  const imageSrc = useMemo(() => {
    if (productVariant.images && productVariant.images.length > 0) {
      return productVariant.images[0].url
    } else {
      return placeholderImage
    }
  }, [productVariant, placeholderImage])

  //const { width } = useWindowDimensions()
  const {lg: lg} = useBreakpoints()

  const handleImageError = () => {
    if (revalidateProductsCache) {
      revalidateProductsCache()
    }
  }

  useEffect(() => {
    // restorationRef is only provided to the ProductCard that needs to be scrolled to
    if (!restorationRef) {
      return
    }
    // Restoring scroll here ensures the previously selected product will always be restored, no matter how long the API request to get products takes
    restorationRef?.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    })
  }, [restorationRef])

  // useEffect if witdh is less than 1024px then set isCardSelected to true
  useEffect(() => {
    if (typeof window !== 'undefined' && !lg) {
      setIsCardSelected(true)
    }
  }, [lg])

  const addToCart = async () => {
    if (data) {
      setLoading(true)
      try {
        await addItemCart({
          productId: String(productVariant.productId),
          variantId: String(productVariant.id),
        })

        gtagEvent('add_to_cart', productVariant)

        router.push('/cart')
      } catch (err) {
        setLoading(false)
      }
    }
  }

  const [hasAugmentedReality, setHasAugmentedReality] = useState(false);
  const [hasCanTryOn, setHasCanTryOn] = useState(false);
  const [hasAvailableIframe, setHasAvailableIframe] = useState(false);
  const [isCanTryOnNotDefined, setIsCanTryOnNotDefined] = useState(false);
  const [isAvailableIframeNotDefined, setIsAvailableIframeNotDefined] = useState(false);

  useEffect(() => {
    const augmentedReality = productVariant.properties.some(
      (property) => property.name?.toLowerCase() === 'augmented-reality' && property.value?.toLowerCase() === 'yes'
    );

    const canTryOn = productVariant.properties.some(
      (property) => property.name?.toLowerCase() === 'can-try-on' && property.value?.toLowerCase() === 'yes'
    );

    const availableIframe = productVariant.properties.some(
      (property) => property.name?.toLowerCase() === 'can-interactive-view' && property.value?.toLowerCase() === 'yes'
    );

    const tryOnNotDefined = !productVariant.properties.some(
      (property) => property.name?.toLowerCase() === 'can-try-on'
    );

    const iframeNotDefined = !productVariant.properties.some(
      (property) => property.name?.toLowerCase() === 'can-interactive-view'
    );

    setHasAugmentedReality(augmentedReality);
    setHasCanTryOn(canTryOn);
    setHasAvailableIframe(availableIframe);
    setIsCanTryOnNotDefined(tryOnNotDefined);
    setIsAvailableIframeNotDefined(iframeNotDefined);

    
  }, [productVariant, productVariant.properties]); 

  const tryOnClassName = hasAugmentedReality && (hasAvailableIframe || isAvailableIframeNotDefined)
    ? "z-20 absolute top-4 left-12 md:top-8 md:left-[66px]"
    : "z-20 absolute top-4 left-4 md:top-8 md:left-8";


  const { price } = usePrice({
    amount: productVariant.price.value,
    baseAmount: productVariant.price.retailPrice,
    currencyCode: productVariant.price.currencyCode!,
    withDecimals: true,
  })

  const relatedProductClassName = cn(
    s.root,
    { [s.slim]: cardStyle === 'slim', [s.simple]: cardStyle === 'simple' },
    className
  )

  const getProductName = (productVariant: ProductVariant) => {
    const productOption = productVariant.options.filter(
      (op) => op.displayName === 'Metal'
    )[0]?.values[0]?.label
    return (
      <div>
        <p className="text-sm leading-5 font-normal capitalize">{`${productOption}`}</p>
        <p className="text-sm leading-5 font-normal capitalize h-10 md:h-fit">{`${productVariant.name}`}</p>
      </div>
    )
  }

  const settings = {
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    dotsClass: 'button__bar_product_card',
  }

  const cardComponent = (
    <div ref={restorationRef ? restorationRef : undefined}>
      {type === 'relatedProducts' && (
        <div
          className={relatedProductClassName}
          aria-label={productVariant.name}
        >
          {cardStyle === 'slim' && (
            <>
              <div className={s.header}>
                <span>{productVariant.name}</span>
              </div>
              {productVariant?.images && (
                <div>
                  <Image
                    quality="85"
                    loading="lazy"
                    loader={imageLoader}
                    src={imageSrc}
                    alt={productVariant.name || 'Product Image'}
                    height={320}
                    width={320}
                    layout="fixed"
                    onError={handleImageError}
                    {...imgProps}
                  />
                </div>
              )}
            </>
          )}

          {cardStyle === 'simple' && (
            <>
              {process.env.COMMERCE_WISHLIST_ENABLED && (
                <WishlistButton
                  className={s.wishlistButton}
                  productId={productVariant.productId}
                  variantId={productVariant.id}
                />
              )}
              {!noNameTag && (
                <div className={s.header}>
                  <h3 className={s.name}>
                    <span>{productVariant.name}</span>
                  </h3>
                  <div className={s.price}>{`${price}`}</div>
                </div>
              )}
              <div className={s.imageContainer}>
                {productVariant?.images && (
                  <div>
                    <Image
                      alt={productVariant.name || 'Product Image'}
                      loading="lazy"
                      className={s.productImage}
                      loader={imageLoader}
                      src={imageSrc}
                      height={540}
                      width={540}
                      quality="85"
                      layout="responsive"
                      onError={handleImageError}
                      {...imgProps}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {cardStyle === 'default' && (
            <>
              {process.env.COMMERCE_WISHLIST_ENABLED && (
                <WishlistButton
                  className={s.relatedWishlistButton}
                  productId={productVariant.productId}
                  variantId={productVariant.id}
                />
              )}
              {/* <ProductTag
                name={productVariant.name}
                price={`${price}`}
                type="relatedProducts"
              /> */}
              <div className={s.imageContainer}>
                {productVariant?.images && (
                  <div>
                    <Image
                      alt={productVariant.name || 'Product Image'}
                      loading="lazy"
                      className={`${s.productImage}`}
                      loader={imageLoader}
                      src={imageSrc}
                      height={540}
                      width={540}
                      quality="85"
                      layout="responsive"
                      onError={handleImageError}
                      {...imgProps}
                    />
                  </div>
                )}
              </div>
              <div className="text-xs lg:text-sm text-center mt-3">
                <p>{price}</p>
                <p>{productVariant.name}</p>
              </div>
            </>
          )}
        </div>
      )}
      {type === 'default' && (
        <Link href={`/product/${productVariant.slug}`}>
          <a
            className={s.defaultCard}
            aria-label={productVariant.name}
            onMouseEnter={() => {
              setIsCardSelected(true)
            }}
            onMouseLeave={() => {
              setIsCardSelected(false)
            }}
            onClick={onSelect ? () => onSelect(productVariant.id) : undefined}
          >
            {hasAugmentedReality && (hasCanTryOn || isCanTryOnNotDefined) && (
              <div className={tryOnClassName}>
                <div className='p-[5px] md:p-1.5 rounded-full bg-[#FFF] text-accent-9 flex items-center justify-center font-semibold cursor-pointer text-sm'>
                  <TryOn />
                </div>
              </div>
            )}

            {hasAugmentedReality && (hasAvailableIframe || isAvailableIframeNotDefined) && (
              <div className="z-20 absolute top-4 left-4 md:top-8 md:left-8">
                <div className='p-[5px] md:p-1.5 rounded-full bg-[#FFF] text-accent-9 flex items-center justify-center font-semibold cursor-pointer text-sm'>
                  <InteractiveView />
                </div>
              </div>
            )}
            <div className="z-20 absolute top-4 right-4 md:top-8 md:right-8">
              {process.env.COMMERCE_WISHLIST_ENABLED && (
                <WishlistButton
                  className={cn(s.wishlistButton)}
                  productId={productVariant.productId}
                  variantId={productVariant.id}
                  isProductCard={true}
                />
              )}
            </div>
            <div className={s.defaultCardContainer}>
              {productVariant?.images && productVariant?.images.length > 0 && (
                <Slider
                  dots={isCardSelected}
                  arrows={isCardSelected}
                  className="text-center !h-[188px] md:!h-[280px]"
                  {...settings}
                >
                  {productVariant.images
                    .slice(0, 3)
                    .map((image, index: number) => (
                      <div
                        key={index}
                        className="relative !w-[188px] !h-[188px] md:!w-[280px] md:!h-[280px]"
                      >
                        <Image
                          loading={index > 0 ? 'lazy' : undefined}
                          loader={imageLoader}
                          src={
                            imgError
                              ? placeholderImage
                              : image.url || placeholderImage
                          }
                          alt={`${productVariant.name}-image-${index + 1}`}
                          layout="fill"
                          objectFit="contain"
                          priority={index === 0}
                          placeholder="blur"
                          blurDataURL="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
                          onError={() => setImgError(true)}
                        />
                      </div>
                    ))}
                </Slider>
              )}
            </div>
            <div className="mt-6 w-fit mx-auto">
              <div className="text-xs uppercase text-center pb-5 text-primary">
                {getProductName(productVariant)}
              </div>
              <div>
                <div className="text-center">
                  <span className="text-sm font-light leading-5 text-primary">{`${price}${productVariant &&
                    productVariant.cyoType &&
                    productVariant.cyoType === 'setting'
                    ? ' (Setting Price)'
                    : ''
                    }`}</span>
                </div>
              </div>
              {isCardSelected && !inWishlist && (
                <div className="mt-5 flex items-center justify-center z-20">
                  <div className='md:hidden'>
                    <Button
                      variant="type1"
                      onClick={() =>
                        router.push(`/product/${productVariant.slug}`)
                      }
                    >
                      See the Product
                    </Button>
                  </div>
                  <div className='hidden md:block'>
                    <Button
                      variant="type2"
                      onClick={() =>
                        router.push(`/product/${productVariant.slug}`)
                      }
                    >
                      See the Product
                    </Button>
                  </div>
                </div>
              )}
              {isCardSelected && inWishlist && (
                <div className="mt-5 flex items-center justify-center z-20">
                  {productVariant.cyoType === 'setting' ? (
                    <>
                    <div className='md:hidden'>
                      <Button
                        variant="type1"
                        onClick={() =>
                          router.push(`/product/${productVariant.slug}`)
                        }
                      >
                        See the Product
                      </Button>
                    </div>
                    <div className='hidden md:block'>
                      <Button
                        variant="type2"
                        onClick={() =>
                          router.push(`/product/${productVariant.slug}`)
                        }
                      >
                        See the Product
                      </Button>
                    </div>
                    </>
                  ) : (
                    <>
                    <div className='md:hidden '>
                    <Button
                      variant="type1"
                      onClick={() => addToCart()}
                      loading={loading}
                      disabled={loading}
                    >
                      Add To Bag
                    </Button>
                    </div>
                    <div className='hidden md:block'>
                    <Button
                      variant="type2"
                      onClick={() => addToCart()}
                      loading={loading}
                      disabled={loading}
                      >
                      Add To Bag
                    </Button>
                    </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </a>
        </Link>
      )}
    </div>
  )

  // The 'noLinkWrapper' prop is necessary to render the Card without a Link Next.js component.
  // This is helpful when using this component inside a TanStack React Table. See issue --> https://github.com/TanStack/table/issues/89
  if (noLinkWrapper) {
    return cardComponent
  } else {
    return <div>{cardComponent}</div>
  }
}

export default ProductVariantCard
